import { useEffect, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";

import { glencocoClientAPI } from "@/api/glencoco";
import { dd } from "@/helpers/datadog";

import { INSUFFICIENT_OAUTH_SCOPES_ERROR_CODE } from "../constants";
import { DetectedMeetingI } from "../types";
import {
  LOOKALIKE_SYNC_WINDOW_MS,
  OAUTH_DETECT_POLL_MS,
  OAUTH_DETECT_START_DELAY_MS,
} from "../utils/lookalike-meeting-sync";

interface UseOAuthMeetingDetectionArgs {
  campaignId: string | undefined;
  accountId: string | undefined;
  contactEmail: string | undefined;
  /**
   * The account's AE. When set, detection targets that AE's calendar; when
   * absent the backend routes (round-robin / default AE).
   */
  aeId: string | undefined;
  /** Lookalike campaign — the only place this second-tier fallback runs. */
  enabled: boolean;
  /** The SDR reached AutoSyncStep — start the delay timer and poll. */
  active: boolean;
}

interface OAuthDetectResult {
  found: boolean;
  detectedMeeting: DetectedMeetingI | null;
  /** 403 insufficient calendar scope — terminal, stop polling. */
  scopeBlocked: boolean;
}

const NOT_FOUND: OAuthDetectResult = {
  found: false,
  detectedMeeting: null,
  scopeBlocked: false,
};

/**
 * Second-tier detection for lookalike bookings: reads the AE's connected
 * calendar via the existing detect_meeting endpoint, as a fallback for when the
 * ICS invite never syncs (no guest added, or the provider can't carry one). It
 * waits {@link OAUTH_DETECT_START_DELAY_MS} so the ICS channel gets first shot,
 * then polls until the event turns up, the AE's grant is missing calendar
 * scope, or the shared {@link LOOKALIKE_SYNC_WINDOW_MS} elapses. A missing
 * calendar connection returns `found:false` from the backend, so this stays
 * quiet (no error spam) for AEs without OAuth.
 */
export const useOAuthMeetingDetection = ({
  campaignId,
  accountId,
  contactEmail,
  aeId,
  enabled,
  active,
}: UseOAuthMeetingDetectionArgs): {
  found: boolean;
  detectedMeeting: DetectedMeetingI | null;
} => {
  const [pollingStarted, setPollingStarted] = useState(false);
  const [isResolved, setIsResolved] = useState(false);
  // One-shot telemetry guards (reset when detection ends).
  const startedEmittedRef = useRef(false);
  const terminalEmittedRef = useRef(false);
  const errorEmittedRef = useRef(false);

  // When detection begins: after a short delay let OAuth start polling (so the
  // ICS channel gets first shot), and hard-stop the whole channel once the
  // shared window elapses. Reset both when detection ends.
  useEffect(() => {
    if (!(active && enabled)) {
      setPollingStarted(false);
      setIsResolved(false);
      startedEmittedRef.current = false;
      terminalEmittedRef.current = false;
      errorEmittedRef.current = false;

      return;
    }

    const startTimer = setTimeout(() => {
      setPollingStarted(true);
      if (!startedEmittedRef.current) {
        startedEmittedRef.current = true;
        dd.rum.log("lookalike_detect.oauth.started", {
          campaign_id: campaignId,
          account_id: accountId,
          tier: "oauth",
        });
      }
    }, OAUTH_DETECT_START_DELAY_MS);
    const windowTimer = setTimeout(
      () => setIsResolved(true),
      LOOKALIKE_SYNC_WINDOW_MS
    );

    return () => {
      clearTimeout(startTimer);
      clearTimeout(windowTimer);
    };
  }, [active, enabled, campaignId, accountId]);

  const canPoll =
    enabled &&
    active &&
    pollingStarted &&
    !isResolved &&
    !!campaignId &&
    !!accountId &&
    !!contactEmail;

  const query = useQuery<OAuthDetectResult>({
    queryKey: [
      "oauth-meeting-detect",
      campaignId,
      accountId,
      contactEmail,
      aeId,
    ],
    enabled: canPoll,
    refetchInterval: canPoll ? OAUTH_DETECT_POLL_MS : false,
    // A not-yet-visible event is the expected case, not stale data — don't
    // refetch on window focus.
    refetchOnWindowFocus: false,
    queryFn: async (): Promise<OAuthDetectResult> => {
      if (!campaignId || !accountId || !contactEmail) {
        return NOT_FOUND;
      }

      const API = glencocoClientAPI();
      const response = await API.scheduling.detectMeetingMultiAE(
        campaignId,
        accountId,
        {
          contact_email: contactEmail,
          ae_ids: aeId ? [aeId] : undefined,
        }
      );

      // The response interceptor returns AxiosErrors instead of throwing.
      if (axios.isAxiosError(response)) {
        const errorCode = response.response?.data?.error_code;
        const scopeBlocked =
          response.response?.status === 403 &&
          errorCode === INSUFFICIENT_OAUTH_SCOPES_ERROR_CODE;

        // Insufficient-scope is a terminal outcome surfaced below; any other
        // error is a genuine failure worth one RUM error per detection session.
        if (!scopeBlocked && !errorEmittedRef.current) {
          errorEmittedRef.current = true;
          dd.rum.error("lookalike_detect.oauth.error", {
            campaign_id: campaignId,
            account_id: accountId,
            tier: "oauth",
            status: response.response?.status,
            error_code: errorCode,
          });
        }

        return { ...NOT_FOUND, scopeBlocked };
      }

      if (response.data.found && response.data.meeting_time) {
        return {
          found: true,
          scopeBlocked: false,
          detectedMeeting: {
            ae_id: response.data.ae_id,
            meeting_time: response.data.meeting_time,
            event_id: response.data.event_id,
            attendees: response.data.attendees,
          },
        };
      }

      return NOT_FOUND;
    },
  });

  const result = query.data ?? NOT_FOUND;

  // Stop polling as soon as the calendar returns the event or a terminal scope
  // error. The 5-minute window is bounded by the timer above: not-found polls
  // keep the same result reference (React Query structural sharing), so they
  // can't drive this effect.
  useEffect(() => {
    if (active && enabled && (result.found || result.scopeBlocked)) {
      setIsResolved(true);
    }
  }, [active, enabled, result]);

  // One-shot terminal telemetry: fires when detection resolves (found /
  // scope-blocked) or the window timer times out, so the OAuth tier's outcome
  // per booking is visible in RUM.
  useEffect(() => {
    if (!(active && enabled) || !isResolved || terminalEmittedRef.current) {
      return;
    }

    terminalEmittedRef.current = true;
    const outcome = result.found
      ? "found"
      : result.scopeBlocked
        ? "scope_blocked"
        : "timeout";
    dd.rum.log(`lookalike_detect.oauth.${outcome}`, {
      campaign_id: campaignId,
      account_id: accountId,
      tier: "oauth",
      ...(result.found
        ? { meeting_time: result.detectedMeeting?.meeting_time }
        : {}),
    });
  }, [active, enabled, isResolved, result, campaignId, accountId]);

  return { found: result.found, detectedMeeting: result.detectedMeeting };
};
